import React, { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { Button, showErrorToast, showSuccessToast } from '@/components';
import { ApiKeyEnvironment } from '@/services/api/apiKeys';
import {
  disableWebhookEndpoint,
  listWebhookEndpoints,
  WebhookEndpointRow,
} from '@/services/api/webhooks';
import { extractApiErrorMessage } from '@/utils/errorHandler';
import { EnvBadge } from './index';
import AddWebhookEndpointModal from './AddWebhookEndpointModal';
import DisableWebhookEndpointModal from './DisableWebhookEndpointModal';

const formatTime = (value: string | null) =>
  value ? dayjs(value).format('DD MMM YYYY, HH:mm') : '—';

// ─── One endpoint row ─────────────────────────────────────────────────
const EndpointCard: React.FC<{
  row: WebhookEndpointRow;
  onDisable: (row: WebhookEndpointRow) => void;
}> = ({ row, onDisable }) => {
  const disabled = row.disabledAt != null;

  return (
    <div
      className={`rounded-xl border border-stroke-soft-200 bg-bg-white-0 px-5 py-4 ${
        disabled ? 'opacity-70' : ''
      }`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="break-all font-mono text-sm font-semibold text-text-strong-950">
          {row.url}
        </span>
        <EnvBadge environment={row.environment} />
        {disabled && (
          <span className="rounded-full bg-error-lighter px-2 py-0.5 text-[11px] font-semibold text-error-dark">
            Disabled
          </span>
        )}
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-text-sub-600">
        <span>Created: {formatTime(row.createdAt)}</span>
        {disabled && <span>Disabled: {formatTime(row.disabledAt)}</span>}
      </div>

      {!disabled && (
        <div className="mt-3">
          <Button variant="white" size="small" onClick={() => onDisable(row)}>
            <i className="ri-forbid-line mr-1.5 text-error-base" />
            <span className="text-error-base">Disable</span>
          </Button>
        </div>
      )}
    </div>
  );
};

/**
 * Webhooks live on the same Developer page as API keys and follow the same
 * per-environment wall: a Sandbox endpoint only receives sandbox events, a
 * Production endpoint only production events. Endpoint management is live;
 * the delivery pipeline itself is rolling out — registered endpoints start
 * receiving events as soon as it ships.
 */
const WebhooksSection: React.FC = () => {
  const [endpoints, setEndpoints] = useState<WebhookEndpointRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [disableTarget, setDisableTarget] = useState<WebhookEndpointRow | null>(
    null
  );

  const load = useCallback(async () => {
    try {
      setEndpoints(await listWebhookEndpoints());
    } catch (err) {
      showErrorToast(
        'Failed to load webhook endpoints',
        extractApiErrorMessage(err, 'Failed to load webhook endpoints')
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleDisable = async (row: WebhookEndpointRow) => {
    try {
      await disableWebhookEndpoint(row.id);
      showSuccessToast(
        'Webhook endpoint disabled',
        'It stops receiving events immediately'
      );
      setDisableTarget(null);
      load();
    } catch (err) {
      showErrorToast(
        'Failed to disable endpoint',
        extractApiErrorMessage(err, 'Failed to disable endpoint')
      );
    }
  };

  const byEnv = (env: ApiKeyEnvironment) =>
    endpoints.filter((e) => e.environment === env);
  // One active endpoint per environment (the backend enforces it too).
  const activeByEnv: Partial<Record<ApiKeyEnvironment, WebhookEndpointRow>> =
    {};
  for (const e of endpoints) {
    if (!e.disabledAt && !activeByEnv[e.environment]) {
      activeByEnv[e.environment] = e;
    }
  }
  const bothTaken = !!activeByEnv.LIVE && !!activeByEnv.TEST;

  return (
    <section className="rounded-2xl border border-stroke-soft-200 bg-bg-white-0 p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-text-strong-950">
            Webhooks · Shipment
          </h2>
          <p className="mt-0.5 max-w-xl text-xs text-text-sub-600">
            Get every shipment event pushed to your endpoint instead of polling:
            what happened, with a readable message and its photos, plus the
            shipment as it stood at that moment. Every delivery is signed with
            the endpoint&apos;s secret (shown once at registration) — verify the{' '}
            <code className="font-mono">x-dash-signature</code> header, dedupe
            on <code className="font-mono">event.id</code>, and order by{' '}
            <code className="font-mono">event.sequence</code>. See the API docs
            tab.
          </p>
        </div>
        <Button onClick={() => setAddOpen(true)} disabled={bothTaken}>
          <i className="ri-add-line mr-1.5" />
          Add endpoint
        </Button>
      </div>

      {/* Environment wall — same rule as keys. */}
      <div className="mt-4 flex items-start gap-2.5 rounded-lg bg-information-lighter px-4 py-3 text-xs text-information-dark">
        <i className="ri-shield-keyhole-line mt-px text-sm" />
        <span>
          <b>Production and Sandbox are separate environments.</b> A Sandbox
          endpoint receives only sandbox events, a Production endpoint only
          production events. One active endpoint per environment — to move to a
          new URL, disable the current one first.
          {bothTaken && (
            <> Both environments have their endpoint, so adding is off.</>
          )}
        </span>
      </div>

      {loading ? (
        <p className="mt-6 text-sm text-text-soft-400">Loading endpoints…</p>
      ) : endpoints.length === 0 ? (
        <div className="mt-6 rounded-xl border border-dashed border-stroke-sub-300 px-6 py-10 text-center">
          <i className="ri-broadcast-line text-2xl text-text-soft-400" />
          <p className="mt-2 text-sm font-medium text-text-strong-950">
            No webhook endpoints yet
          </p>
          <p className="mt-1 text-xs text-text-sub-600">
            Add a Sandbox endpoint first to build your handler, then a
            Production endpoint to go live.
          </p>
        </div>
      ) : (
        <div className="mt-6 space-y-6">
          {(['LIVE', 'TEST'] as const).map((env) => {
            const rows = byEnv(env);
            if (rows.length === 0) return null;
            return (
              <div key={env}>
                <h3 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-text-soft-400">
                  <EnvBadge environment={env} />
                </h3>
                <div className="space-y-3">
                  {rows.map((row) => (
                    <EndpointCard
                      key={row.id}
                      row={row}
                      onDisable={setDisableTarget}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <AddWebhookEndpointModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={load}
        activeByEnv={activeByEnv}
      />
      <DisableWebhookEndpointModal
        row={disableTarget}
        onClose={() => setDisableTarget(null)}
        onConfirm={handleDisable}
      />
    </section>
  );
};

export default WebhooksSection;
